/**
 * MeetingRecorder
 *
 * Records the meeting by capturing the screen/tab itself (getDisplayMedia) — this
 * always matches whatever is actually on screen (video tiles, chat, etc.) with no
 * separate layout logic to keep in sync. Tab/system audio (covers remote
 * participants, whose audio plays back through the page) is mixed with the host's
 * own microphone (which getDisplayMedia's audio does NOT include, since you don't
 * hear yourself) via the Web Audio API into a single recorded audio track.
 *
 * Usage:
 *   const rec = new MeetingRecorder();
 *   await rec.start(localStream); // prompts the browser's "choose what to share" dialog
 *   ...
 *   const blob = await rec.stop(); // Blob | null
 */
export class MeetingRecorder {
  constructor() {
    this._recorder = null;
    this._chunks = [];
    this._screenStream = null;
    this._audioCtx = null;
    this._destination = null;
    this._micSource = null;
    this._tabAudioSource = null;
  }

  get isRecording() {
    return !!this._recorder && this._recorder.state !== 'inactive';
  }

  /** @param {MediaStream|null} localStream — mixed in for the host's own mic audio */
  async start(localStream) {
    if (this.isRecording) return;
    if (!navigator.mediaDevices?.getDisplayMedia) {
      throw new Error('Screen recording is not supported in this browser.');
    }

    // Prompts the browser's native "choose what to share" dialog, hinted to default to
    // "Entire Screen" (same displaySurface hint used by the in-call screen-share
    // feature) — the user still has to confirm, and can pick a window/tab instead.
    this._screenStream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: 'monitor', frameRate: 15 },
      audio: true,
      systemAudio: 'include',
    });

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    this._audioCtx = new AudioCtx();
    this._destination = this._audioCtx.createMediaStreamDestination();

    // Mix in the host's own microphone — not present in the tab/screen capture's audio.
    if (localStream && localStream.getAudioTracks().length > 0) {
      try {
        this._micSource = this._audioCtx.createMediaStreamSource(new MediaStream(localStream.getAudioTracks()));
        this._micSource.connect(this._destination);
      } catch { /* ignore */ }
    }

    // Mix in captured tab/system audio — covers remote participants (their audio
    // plays through the page) when the browser/OS supports sharing audio.
    const screenAudioTracks = this._screenStream.getAudioTracks();
    if (screenAudioTracks.length > 0) {
      try {
        this._tabAudioSource = this._audioCtx.createMediaStreamSource(new MediaStream(screenAudioTracks));
        this._tabAudioSource.connect(this._destination);
      } catch { /* ignore */ }
    }

    const combined = new MediaStream([
      ...this._screenStream.getVideoTracks(),
      ...this._destination.stream.getAudioTracks(),
    ]);

    // Prefer mp4 (Safari, and recent Chrome/Edge with hardware H.264) for a widely
    // compatible download; fall back to webm where MediaRecorder can't do mp4.
    const mimeType = [
      'video/mp4;codecs=avc1,mp4a.40.2',
      'video/mp4',
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
    ].find(t => window.MediaRecorder?.isTypeSupported?.(t));

    this._recorder = new MediaRecorder(combined, mimeType ? { mimeType } : undefined);
    this._chunks = [];
    this._recorder.ondataavailable = e => {
      if (e.data && e.data.size > 0) this._chunks.push(e.data);
    };
    this._recorder.start(1000);

    // If the user stops sharing via the browser's own "Stop sharing" bar, end the recording too.
    this._screenStream.getVideoTracks()[0]?.addEventListener('ended', () => {
      if (this.isRecording) this.stop();
    });
  }

  /** @returns {Promise<Blob|null>} */
  stop() {
    return new Promise(resolve => {
      if (!this._recorder || this._recorder.state === 'inactive') {
        this._cleanup();
        resolve(null);
        return;
      }
      this._recorder.onstop = () => {
        const blob = this._chunks.length
          ? new Blob(this._chunks, { type: this._recorder.mimeType || 'video/webm' })
          : null;
        this._cleanup();
        resolve(blob);
      };
      this._recorder.stop();
    });
  }

  _cleanup() {
    this._screenStream?.getTracks().forEach(t => t.stop());
    this._screenStream = null;
    this._micSource?.disconnect();
    this._micSource = null;
    this._tabAudioSource?.disconnect();
    this._tabAudioSource = null;
    this._audioCtx?.close().catch(() => {});
    this._audioCtx = null;
    this._recorder = null;
  }
}
